import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { switchMap, map, first, filter, take, concatMap } from 'rxjs/operators';

import { GA4AnalyticsPublicActions, GA4AnalyticsPublicConstants } from '../../../public.api';

import { ApiSizeInfo, CartItem } from '@api/cart/cart.models';
import { getPage, getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { ProductPublicModels } from '@features/products/product';
import { isBuyNow } from '@features/sidebar/infrastructure/state/sidebar.selectors';
import { WishlistPublicActions } from '@features/wishlist';
import { GiftCardActions } from '@pages/gift-card/store/gift-card.actions';
import { GiftCardSelectors } from '@pages/gift-card/store/gift-card.selector';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import * as AnalyticsUtils from '@shared/analytics/COMMON/services/analytics.utils';
import { Ga4EventLocation } from '@shared/analytics/GA4/models/event-data.model';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { Ga4SendDatalayer, Ga4Event } from '@shared/analytics/GA4/store/ga4-analytics.actions';
import { GoToCheckout, GoToSection, NavigationConstants } from '@shared/navigation';
import { RelatedProductType } from '@shared/product/related-products/domain/models/related-products.models';
import {
    AdjustItemQuantityShopcart,
    AdjustItemSizeShopcart,
    DeleteItemsShopcart,
    LoadShopCartSuccess,
    SetShopCartSuccess,
} from '@shared/shopcart/store/shopcart.actions';
import {
    AddProductsShopcartCoreSuccess,
    LoadShopCartCoreSuccess,
    SetItemsInCartSuccess,
} from '@shared/shopcart-core/infrastructure/state/shopcart-core.actions';
import { getPendingGetCartCore, getShopCartCoreIsLoaded, getShopCartCoreItems } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';
import { OpenModalFromComponent } from '@ui/dialog/store/modal.actions';

@Injectable()
export class GA4AnalyticsCartEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly storageService: CoreStorageService,
        private readonly translateService: TranslateService,
    ) { }

    public EventViewCartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Ga4SendDatalayer),
            filter(({ dataLayer }) =>
                // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
                dataLayer.event === GA4EventType.page_view && /.*(cesta|minicesta).*/.test(dataLayer.context.page_title)),
            concatLatestFrom(() => this.handleViewCartEvent()),
            switchMap(() =>
                this.store.select(getShopCartCoreIsLoaded).pipe(
                    filter(isLoaded => !!isLoaded),
                    take(1),
                )),
            concatLatestFrom(() => this.store.select(getShopCartCoreItems)),
            filter(([_, shopcartItems]) => !!shopcartItems?.length),
            map(() => Ga4Event({
                name: GA4EventType.view_cart,
                eventFrom: EventFrom.shopcart,
            })),
        );
    });

    private handleViewCartEvent() {
        return this.store.select(getPendingGetCartCore).pipe(
            switchMap(pendingItems => {
                if (!pendingItems.length) return of(true);
                return this.actions$.pipe(ofType(SetItemsInCartSuccess)).pipe(first());
            }),
        );
    }

    public EventAddItemWishlistGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WishlistPublicActions.AddItemsWishlistAction),
            concatLatestFrom(() => this.store.select(getPage)),
            map(([action, page]) => {
                const eventFromParser = {
                    ItxCategoryPage: EventFrom.parrilla,
                    ItxProductPage: EventFrom.productdetail,
                    ItxAngularShopcartPage: EventFrom.shopcart,
                };

                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_wishlist,
                    eventFrom: eventFromParser[page],
                    data: {
                        items: action.items.map(item => item.item) as unknown as ProductPublicModels.PublicProduct[],
                    },
                });
            }),
        );
    });

    private mapProductsWithRelatedType(
        products: ProductPublicModels.PublicProduct[],
        location: string,
        parentType?: RelatedProductType,
    ): ProductPublicModels.PublicProduct[] {
        if (products?.length && location === 'related_product' && !!parentType) {
            return products.map(product => ({
                ...product,
                relatedType: parentType,
            }));
        }
        return products;
    }

    public EventAddItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom(() => this.store.select(getPage)),
            map(([action, page]) => {
                const eventFrom = AnalyticsUtils.getEventFromByLocation(
                    action.location,
                    action.parentType,
                    action.eventFrom,
                );
                const eventFromParser = {
                    ItxCategoryPage: EventFrom.parrilla,
                    ItxProductPage: EventFrom.parrilla,
                    productos_relacionados: action?.parentType,
                };
                const products: ProductPublicModels.PublicProduct[] = this.mapProductsWithRelatedType(
                    action.products,
                    action.location,
                    action?.parentType,
                );

                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_cart,
                    eventFrom: eventFromParser[eventFrom]
                        || eventFrom
                        || eventFromParser[page]
                        || EventFrom.shopcart,
                    data: {
                        imBundleReference: products[0]?.bundleReference || undefined,
                        items: products,
                        place: action?.place,
                        relatedsSectionId: action?.relatedsSectionId,
                    },
                });
            }),
        );
    });

    public EventDeleteItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemsShopcart),
            map((action) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.remove_from_cart,
                eventFrom: EventFrom.shopcart,
                data: {
                    items: action.items,
                    imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.eliminar,
                },
            })),
        );
    });

    public EventEditRemoveItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            map(({ item }) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.remove_from_cart,
                    eventFrom: EventFrom.shopcart,
                    data: {
                        items: [{ ...item }],
                        imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.modificar_producto_cesta_ok,
                    },
                });
            }),
        );
    });

    public EventEditAddItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            map(({ item, size }) => {
                const addedProduct: ApiSizeInfo = {
                    ...item.colors.find((color) => color.id === item.colorId)?.sizes
                        .find((_size) => _size.sku === size.sku),
                };
                // biome-ignore lint/performance/noDelete: <explanation>
                delete addedProduct.name;
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_cart,
                    eventFrom: EventFrom.shopcart,
                    data: {
                        items: [{ ...item, ...addedProduct } as unknown as CartItem],
                        imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.modificar_producto_cesta_ok,
                    },
                });
            }),
        );
    });

    public EventQuantityAddRemoveItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemQuantityShopcart),
            filter(({ quantity, item }) => quantity !== item.quantity),
            map((action) => GA4AnalyticsPublicActions.Ga4Event({
                name: action.quantity > action.item.quantity
                    ? GA4EventType.add_to_cart : GA4EventType.remove_from_cart,
                eventFrom: EventFrom.shopcart,
                data: {
                    items: [{
                        ...action.item,
                        quantity: Math.abs(action.quantity - action.item.quantity),
                    }],
                },
            })),
        );
    });

    public EventAddToCartGiftcardGA4$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(GiftCardActions.AddToShopCartSuccess),
                concatLatestFrom(() => [
                    this.store.select(GiftCardSelectors.getCard),
                    this.store.select(GiftCardSelectors.getSelectedGiftCardPrice),
                ]),
                map(([, giftCardData, giftCardPrice]) => {
                    return GA4AnalyticsPublicActions.Ga4Event({
                        name: GA4EventType.add_to_cart,
                        eventFrom: EventFrom.giftcard,
                        data: {
                            items: [{ ...giftCardData, ...giftCardPrice }],
                            imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.anadir_gift_card_cesta,
                        },
                    });
                }),
            );
        },
    );

    public EventBeginCheckoutGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToSection),
            filter(({ url }) => url === NavigationConstants.checkout || url === NavigationConstants.checkoutAuth),
            filter(() => this.storageService.getValue('cartType') !== 'buy-now'),
            concatLatestFrom(() => [this.store.select(getPage), this.store.select(isBuyNow)]),
            filter(([_, page, buyNow]) => page !== 'ItxFastSintStoreListPage' && !buyNow),
            // REF-AN-1
            concatLatestFrom(() => this.translateService.get('dev.cart.checkout')),
            map(([_, buttonText]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.begin_checkout,
                eventFrom: EventFrom.directo, // NOTE: Recarga de página al acceder al túnel de checkout
                data: {
                    imLinkText: buttonText,
                },
            })),
        );
    });

    public EventBeginCheckoutFastSintGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToSection),
            filter(({ url }) => url === NavigationConstants.fastSintStores),
            concatLatestFrom(() => {
                return this.store.select(getCmsConfigValue('clickandcollect_horamax')).pipe(
                    take(1),
                    map(hour => Number(hour)),
                    switchMap((hour) => {
                        // REF-AN-6
                        if (hour === 1) {
                            return this.translateService.get('dev.shopCart.fastSint.pickupOrderHour', { $0: hour });
                        }
                        return this.translateService.get('dev.shopCart.fastSint.pickupOrderHours', { $0: hour });
                    }),
                );
            }),
            map(([_, buttonText]) =>
                GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.begin_checkout,
                    eventFrom: EventFrom.fastSint,
                    data: {
                        imLinkText: buttonText,
                    },
                })),
        );
    });

    public EventBeginCheckoutLoginGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToCheckout),
            filter(({ checkoutAuth }) => checkoutAuth),
            // REF-AN-1
            concatLatestFrom(() => this.translateService.get('dev.cart.checkout')),
            map(([_, buttonText]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.begin_checkout,
                eventFrom: EventFrom.directo, // NOTE: Recarga de página al acceder al túnel de checkout
                data: {
                    imLinkText: buttonText,
                },
            })),
        );
    });

    public CloseGiftTicketModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenModalFromComponent),
            filter((action) => action.componentName === 'shop-cart-packagin'),
            switchMap(() => this.actions$.pipe(
                ofType(LoadShopCartSuccess),
            ).pipe(first())),
            concatLatestFrom(() => this.store.select(getShopCartCoreItems)),
            filter(([_, shopcartItems]) => !!shopcartItems?.length),
            map(() => GA4AnalyticsPublicActions.Ga4Event({ name: GA4EventType.view_cart, eventFrom: EventFrom['shop-cart-packagin'] })),

        );
    });

    public EventErrorCustomErrorCesta$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartCoreSuccess),
            concatLatestFrom(() => this.store.select(getPage)),
            filter(([{ shopcart }, currentPage]) => currentPage === 'ItxAngularShopcartPage' && !!shopcart?.errors?.length),
            map(([action]) => {
                const errorKey = action.shopcart?.errors?.[0];
                return Ga4Event({
                    name: GA4EventType.error_custom,
                    eventFrom: EventFrom.shopcart,
                    data: {
                        errorData: { error: { key: errorKey } },
                        errorType: 'error_cesta',
                        imEventName: 'form_submit',
                        imLocation: Ga4EventLocation.pagina,
                        imType: 'error_cesta',
                    },
                });
            }),
        );
    });

    public emptyShopcartItemsPageviewGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemsShopcart),
            concatLatestFrom(() => this.store.select(getPage)),
            filter(([_action, page]) => page === 'ItxAngularShopcartPage'),
            concatMap(() => this.actions$.pipe(ofType(SetShopCartSuccess)).pipe(first())),
            filter((action) => action.itemsInCart === 0),
            map(() => Ga4Event({ name: GA4EventType.page_view })),
        );
    });
}
